<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import { navMessages } from "./i18n/nav";
import NavLink from "./NavLink.vue";
import type { NavRow } from "./nav-items";

/**
 * Um grupo do menu: um cabeçalho e as linhas dele.
 *
 * A lateral era uma `<ul>` chapada de dezenove links — ~684px, com o primeiro
 * grupo de entidade do tenant começando por volta dos 854px, abaixo da dobra numa
 * tela de 900px. Recolher é o que devolve essa altura sem esconder nada de
 * ninguém, e quem abre um grupo o encontra aberto amanhã.
 *
 * As LINHAS são todas iguais de propósito: uma tela do produto, uma entidade que
 * este workspace criou e um funil chegam aqui como a mesma `NavRow`. Um grupo que
 * soubesse a diferença seria o começo de três componentes que se parecem. Quem as
 * desenha é o `NavLink`; aqui só se decide QUAIS aparecem.
 *
 * Um grupo vazio nunca se desenha — a regra já valia para as entidades e vale
 * para todos: um cabeçalho sozinho é uma promessa que a lista não cumpre.
 */
const props = defineProps<{
	label: string;
	rows: readonly NavRow[];
	narrow?: boolean;
	/** Presente = recolhível, e a preferência fica gravada com este apelido. */
	slug?: string;
	/** Só vale enquanto ninguém tiver decidido. Depois disso manda o localStorage. */
	defaultOpen?: boolean;
}>();

const emit = defineEmits<{ navigate: [] }>();

/**
 * O rótulo CHEGA desenhado — quem o traduz é quem monta a lista, porque metade
 * dos grupos são do produto e a outra metade são nomes que o tenant escolheu.
 * Aqui só sobra a frase que este componente escreve sozinho: a do leitor de tela.
 */
const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: navMessages });

const route = useRoute();

/**
 * O que esta pessoa já decidiu sobre este grupo, ou `null` enquanto não decidiu.
 *
 * Guardado à parte do estado ABERTO de propósito: o padrão de alguns grupos
 * depende de dado que ainda não chegou — quantos funis este workspace tem só se
 * sabe depois da resposta — e um booleano resolvido uma vez no `setup` congelaria
 * a lista vazia como se fosse a resposta. Enquanto ninguém decidiu, o padrão
 * continua valendo e continua podendo mudar; no instante em que alguém clica, a
 * escolha passa a mandar e o padrão nunca mais é consultado.
 *
 * No mesmo estilo de `benvox:nav-collapsed`, que é a outra preferência da lateral.
 */
const chosen = ref(readStored());

function readStored(): boolean | null {
	// Sem `slug` o grupo não recolhe: é o que o tenant criou, e ele fica à vista.
	if (!props.slug) return true;
	try {
		const stored = localStorage.getItem(`benvox:nav-group:${props.slug}`);
		return stored === null ? null : stored === "1";
	} catch {
		// Navegação privativa lança na leitura. Um padrão não é uma falha.
		return null;
	}
}

const open = computed({
	get: () => chosen.value ?? props.defaultOpen ?? true,
	set: (value) => {
		chosen.value = value;
	},
});

watch(chosen, (value) => {
	if (!props.slug || value === null) return;
	try {
		localStorage.setItem(`benvox:nav-group:${props.slug}`, value ? "1" : "0");
	} catch {
		// Perder a preferência é melhor do que quebrar por ela, e nenhum dos dois
		// merece um aviso na tela.
	}
});

/**
 * O GRUPO DA ROTA ATUAL ABRE SOZINHO — e não fecha nenhum outro.
 *
 * Sem isto, quem chega a uma tela pela paleta, por um link colado ou por um F5
 * dentro de um grupo que fechou ontem lê um menu que não marca lugar nenhum: a
 * linha ativa existe, mas está enrolada atrás de um cabeçalho. É a regra da V2
 * (`SidebarMenu.vue:615-626`), lá feita no `onMounted` e a cada troca de rota.
 *
 * A comparação ignora a query porque dois funis são o MESMO caminho — o que os
 * separa é `?funil=`, e para a pergunta "este grupo contém onde estou" o caminho
 * basta. As `rows` entram no watch porque metade delas chega depois da resposta
 * do servidor: no primeiro tique a lista de funis ainda está vazia.
 */
watch(
	[() => route.path, () => props.rows],
	() => {
		if (!props.slug) return;
		const here = props.rows.some((row) => row.active || row.to.split("?")[0] === route.path);
		if (here) chosen.value = true;
	},
	{ immediate: true },
);

/**
 * Quantas coisas esperando existem lá dentro — e só interessa com o grupo
 * FECHADO.
 *
 * Recolher escondia os badges junto com as linhas, que é o contrário do que um
 * badge existe para fazer: quem fecha "Trabalho" para caber mais na tela não está
 * dizendo que parou de se importar com sete conversas sem resposta. Fechado, o
 * cabeçalho carrega a soma; aberto, cada linha volta a responder por si e repetir
 * o total ali em cima seria dizer duas vezes a mesma coisa.
 */
const waiting = computed(() => props.rows.reduce((total, row) => total + (row.badge ?? 0), 0));
const hidden = computed(() => (open.value ? 0 : waiting.value));

/**
 * O cabeçalho é o degrau ACIMA das linhas e tem de parecer outro nível — mas o
 * degrau é de PESO, não de tamanho: 12,1875px em 600 contra os 13,125px em 500
 * da linha, que são as duas medidas da V2 (`.8125rem` e `.875rem` sobre uma raiz
 * de 15px). O versalete miúdo que morava aqui (`text-3xs uppercase
 * tracking-wider`) fazia o cabeçalho parecer legenda de tabela e deixava a
 * lateral com dois alfabetos.
 *
 * A COR também é a da V2: cinza (`nav-group`) quando o grupo está fechado, quase
 * preto (`nav-group-on`) quando aberto — `.sidebar-group-label` e
 * `.sidebar-group-label--active` lá.
 *
 * `justify-between` é o outro fato da V2: o chevron vai para a PONTA DIREITA da
 * coluna, não colado ao rótulo — colado, ele empurra o texto e cada grupo começa
 * numa abscissa diferente conforme o tamanho do nome.
 */
// padrão Claude (dono, 25/10): cabeçalho pequeno e sempre calado, aberto ou fechado.
const HEAD =
	"relative flex w-full items-center justify-between gap-2 overflow-hidden whitespace-nowrap rounded-lg py-1.5 text-xs font-medium transition-colors";

/**
 * Uma string só: o `class` do `Icon` é uma prop declarada, e prop não é array.
 *
 * Estreito ele NÃO some — vai a `opacity-0` junto do rótulo. Sumir mudava a
 * altura do cabeçalho e a lista inteira pulava ao abrir no hover.
 */
const chevron = computed(() =>
	[
		"size-3 shrink-0 text-nav-group transition-all duration-200",
		open.value ? "" : "-rotate-90",
		props.narrow ? "opacity-0" : "",
	]
		.filter(Boolean)
		.join(" "),
);
</script>

<template>
	<div v-if="rows.length">
		<!-- Recolhível ou não, o cabeçalho é o mesmo texto no mesmo lugar: um grupo
		     que muda de forma ao ganhar um botão faz a lista parecer duas listas. -->
		<component
			:is="slug ? 'button' : 'div'"
			:type="slug ? 'button' : undefined"
			:class="[
				HEAD,
				'text-nav-group',
				slug ? 'hover:bg-nav-hover hover:text-nav-fg' : '',
				narrow ? 'mb-0.5 px-0' : 'mb-0.5 px-3',
			]"
			:aria-expanded="slug ? open : undefined"
			:title="narrow ? label : undefined"
			@click="slug && (open = !open)"
		>
			<!-- Estreito o rótulo fica no DOM e vai a `opacity-0`, como na V2: tirá-lo
			     mudava a ALTURA do cabeçalho, e a lista inteira dava um pulo no
			     instante em que o rail se abria sob o ponteiro. -->
			<span class="truncate" :class="narrow ? 'opacity-0' : ''">{{ label }}</span>

			<!-- O que fica na PONTA DIREITA: o que o grupo esconde e a seta, nessa
			     ordem. A seta é o último caractere da linha em todos os grupos, então
			     ela vira uma coluna — que é o que a torna clicável sem se procurar. -->
			<span class="flex shrink-0 items-center gap-1.5">
				<span
					v-if="hidden && !narrow"
					class="rounded-full bg-bg-muted px-1.5 py-0.5 text-3xs font-semibold text-fg-muted"
				>
					{{ hidden > 99 ? "99+" : hidden }}
				</span>
				<Icon v-if="slug" name="chevronDown" :class="chevron" />
			</span>

			<span
				v-if="hidden && narrow"
				class="absolute right-1.5 size-1.5 rounded-full bg-accent"
				:aria-label="t('nav.waitingIn', { n: hidden, group: label })"
			/>
		</component>

		<ul v-show="open" class="mt-0.5 space-y-0.5">
			<li v-for="row in rows" :key="row.to">
				<NavLink :row="row" :narrow="narrow" @navigate="emit('navigate')" />
			</li>
		</ul>
	</div>
</template>
